/**
 * @module Components.SignUp
 *
 * In-component signup step shown to coaches after the role/about step.
 * Asks how they coach and routes to the matching paywall:
 *   independent  -> PRO   (/trial/pro)
 *   facility/team -> Academy (/trial/academy)
 */
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { cls } from "app/utils";
import styles from "./styles.module.scss";

export type CoachPlanTarget = "pro" | "academy";

type OptionDef = {
  value: CoachPlanTarget;
  icon: string;
  title: string;
  description: string;
};

type PropsDef = {
  onSelectPlan: (target: CoachPlanTarget) => void;
  // Omitted when the step is resumed from a paywall — there is no earlier
  // step to return to, so the Back button is hidden.
  onBack?: () => void;
};

export default function CoachPlanQuestion(props: PropsDef) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<CoachPlanTarget | null>(null);

  // Built inside the component so t() runs in a translation context.
  const options: OptionDef[] = [
    {
      value: "pro",
      icon: "🧑‍🏫",
      title: t("I'm an independent coach"),
      description: t(
        "I coach my own athletes and want tools to grow my business.",
      ),
    },
    {
      value: "academy",
      icon: "🏟️",
      title: t("I run a facility, academy, or team of coaches"),
      description: t(
        "I need scheduling, billing, and a shared calendar across multiple coaches.",
      ),
    },
  ];

  const submit = () => {
    if (selected) {
      props.onSelectPlan(selected);
    }
  };

  return (
    <div className={styles.container}>
      {props.onBack && (
        <button
          type="button"
          className={styles.back}
          onClick={props.onBack}
          aria-label={t("Back")}
        >
          <i className="fas fa-arrow-left" aria-hidden="true" />
          {t("Back")}
        </button>
      )}

      <h2 className={styles.title}>{t("How do you coach?")}</h2>
      <p className={styles.subtitle}>
        {t("This helps us set you up with the right plan — you can switch anytime.")}
      </p>

      <div className={styles.options}>
        {options.map((option) => (
          <button
            key={option.value}
            type="button"
            className={cls(styles.option, {
              [styles.selected]: selected === option.value,
            })}
            onClick={() => setSelected(option.value)}
            aria-pressed={selected === option.value}
          >
            <span className={styles.option_icon}>{option.icon}</span>
            <span className={styles.option_title}>{option.title}</span>
            <span className={styles.option_description}>
              {option.description}
            </span>
          </button>
        ))}
      </div>

      <button
        type="button"
        className="btn btn-primary full"
        onClick={submit}
        disabled={!selected}
      >
        {t("Continue")}
      </button>
    </div>
  );
}
